import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { screeningClearanceRulesAnalysisApi } from '../../../apis';
import { useParams } from 'react-router';

import {
  InternalApiAnalysisRunStatus,
  InternalApiScreeningClearanceRulesAnalysis,
  InternalApiScreeningClearanceRulesAnalysisRun,
  InternalApiEntityType,
} from '../../../api-internal';
import { formatEnumLabel } from '../../../common/helper';
import ScreeningClearanceRulesAnalysisInfo from './ScreeningClearanceRulesAnalysisInfo';
import ScreeningClearanceRulesAnalysisRuns from './ScreeningClearanceRulesAnalysisRuns';
import CreateScreeningClearanceRulesAnalysisRun from './CreateScreeningClearanceRulesAnalysisRun';
import SalvPage from '../../../common/SalvPage';

export function ScreeningClearanceRulesAnalysis() {
  const params = useParams<{
    id: string;
    entity: string;
  }>();
  const id = params.id!;
  const entity = params.entity!;

  const [editing, setEditing] = useState(!id);
  const isMounted = useRef(false);

  const [analysis, setAnalysis] = useState<InternalApiScreeningClearanceRulesAnalysis>();
  const [runs, setRuns] = useState<InternalApiScreeningClearanceRulesAnalysisRun[]>();
  const entityType =
    entity?.toUpperCase() === InternalApiEntityType.PERSON
      ? InternalApiEntityType.PERSON
      : InternalApiEntityType.TRANSACTION;

  const updateAnalysis = (newAnalysis: InternalApiScreeningClearanceRulesAnalysis) => {
    setEditing(false);
    setAnalysis(newAnalysis);
  };

  const fetchAnalysis = useCallback(async () => {
    const analysis = await screeningClearanceRulesAnalysisApi.getAnalysis({
      id: Number.parseInt(id),
    });
    if (isMounted.current) {
      setAnalysis(analysis);
    }
  }, [id]);

  const fetchRuns = useCallback(async () => {
    const runs = await screeningClearanceRulesAnalysisApi.findAllRuns({
      analysisId: Number.parseInt(id),
    });
    setRuns(runs);
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchRuns();
    }
  }, [id, fetchRuns]);

  useEffect(() => {
    let timerId: any;
    const latestRun = getLatestRun(runs);
    if (
      latestRun &&
      (latestRun.status === InternalApiAnalysisRunStatus.RUNNING ||
        latestRun.status === InternalApiAnalysisRunStatus.SCHEDULED)
    ) {
      timerId = setInterval(fetchRuns, 5000);
    } else {
      timerId && clearInterval(timerId);
    }
    return () => timerId && clearInterval(timerId);
  }, [runs, fetchRuns]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (id) {
      fetchAnalysis();
    } else {
      setAnalysis({
        name: '',
        entityType: entityType,
      });
    }
  }, [id, entityType, fetchAnalysis]);

  function getLatestRun(runs?: InternalApiScreeningClearanceRulesAnalysisRun[]) {
    if (runs && runs.length > 0) return runs[0];
  }

  return (
    <SalvPage
      title={
        analysis
          ? analysis.id
            ? `${formatEnumLabel(analysis.entityType)} clearance rules impact analysis`
            : `Add ${analysis.entityType.toLowerCase()} clearance rules impact analysis`
          : ''
      }
    >
      {analysis && (
        <>
          <Row>
            <Col md={runs ? 4 : 12}>
              <ScreeningClearanceRulesAnalysisInfo
                analysis={analysis}
                editing={editing}
                updateAnalysis={updateAnalysis}
                setEditing={setEditing}
              />
            </Col>
            {runs && (
              <>
                <Col md={8}>
                  <ScreeningClearanceRulesAnalysisRuns runs={runs} />
                </Col>
              </>
            )}
          </Row>
          {runs && (
            <CreateScreeningClearanceRulesAnalysisRun
              analysis={analysis}
              latestRun={getLatestRun(runs)}
              onCreate={fetchRuns}
            />
          )}
        </>
      )}
    </SalvPage>
  );
}

export default ScreeningClearanceRulesAnalysis;
